import { useEffect, useState } from 'react';
import { PermissionGuard } from '@/features/auth';
import { useSetAssociationMutation } from '../hooks/useSetAssociationMutation';
import { useReleaseEmailMutation } from '../hooks/useReleaseEmailMutation';
import { DealerSelect } from '@/features/dealers/components/DealerSelect';
import { useDealerQuery } from '@/features/dealers/hooks/useDealerQuery';
import type { Dealer } from '@/features/dealers/types/dealers.types';
import type { AdminUser } from '../types/users.types';
import { Alert, Button, Form, Input, Label } from '@/shared/components';
import { HomeDealerLink } from './HomeDealerLink';

const RELEASE_PHRASE = 'I understand this releases the email for reuse';

interface Props {
  userId: string;
  user: AdminUser;
}

export function UserAssociationPanel({ userId, user }: Props) {
  // Pre-fill query shares dealerKeys.detail(id) with HomeDealerLink. Suppress the
  // global 403 toast here too, otherwise this observer (declared first) can issue
  // the shared request without suppression and toast for a viewer lacking dealer.read.
  const homeDealerQuery = useDealerQuery(user.dealer_id ?? '', { suppressForbiddenToast: true });
  const [selectedDealer, setSelectedDealer] = useState<Dealer | null>(null);
  const [syncedInitial, setSyncedInitial] = useState(false);
  const [orgId, setOrgId] = useState(user.organisation_id ?? '');
  const [assocError, setAssocError] = useState<string | null>(null);

  const [releaseConfirm, setReleaseConfirm] = useState('');
  const [releaseError, setReleaseError] = useState<string | null>(null);

  const assocMutation = useSetAssociationMutation();
  const releaseMutation = useReleaseEmailMutation();

  useEffect(() => {
    if (syncedInitial) return;
    if (!user.dealer_id) {
      setSyncedInitial(true);
      return;
    }
    if (homeDealerQuery.data) {
      setSelectedDealer(homeDealerQuery.data);
      setSyncedInitial(true);
    }
  }, [homeDealerQuery.data, user.dealer_id, syncedInitial]);

  const handleAssocSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAssocError(null);

    if (selectedDealer && orgId) {
      setAssocError('A user can have a dealer or an organisation association, not both.');
      return;
    }

    // Server-side errors surface via the hook-level handleUserMutationError
    // toast. assocError state is reserved for client-side validation only.
    assocMutation.mutate({
      id: userId,
      dealer_id: selectedDealer?.id ?? null,
      organisation_id: orgId || null,
    });
  };

  const handleRelease = () => {
    if (releaseConfirm !== RELEASE_PHRASE) {
      setReleaseError('You must type the confirmation phrase exactly.');
      return;
    }
    setReleaseError(null);
    // Server-side errors toast via the hook; releaseError state is reserved
    // for client-side validation of the confirmation phrase.
    releaseMutation.mutate(
      { id: userId, confirmation: releaseConfirm },
      { onSuccess: () => setReleaseConfirm('') },
    );
  };

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h3 className="font-semibold mb-2">Current Association</h3>
        <div className="p-3 border border-mp-border rounded bg-mp-background text-sm space-y-1">
          <p>
            <span className="font-medium">Home Dealer:</span>{' '}
            <HomeDealerLink dealerId={user.dealer_id} />
          </p>
          <p>
            <span className="font-medium">Organisation ID:</span>{' '}
            {user.organisation_id ?? <span className="text-mp-muted">None</span>}
          </p>
        </div>
      </div>

      <div>
        <h3 className="font-semibold mb-3">Update Association</h3>
        <p className="text-sm text-mp-muted mb-3">
          Set either a Dealer or Organisation association. These are mutually exclusive.
        </p>
        <Form onSubmit={handleAssocSubmit} className="space-y-4">
          {assocError && (
            <Alert variant="error">
              <span>{assocError}</span>
            </Alert>
          )}
          {assocMutation.isSuccess && (
            <Alert variant="success">
              <span>Association updated successfully.</span>
            </Alert>
          )}
          <div>
            <Label htmlFor="dealer-id">Home Dealer</Label>
            <DealerSelect
              selected={selectedDealer}
              onSelect={(dealer) => {
                setSelectedDealer(dealer);
                if (dealer) setOrgId('');
              }}
              placeholder="Search dealers by name, code, city, or postcode..."
            />
          </div>
          <div>
            <Label htmlFor="org-id">Organisation ID (UUID)</Label>
            <Input
              id="org-id"
              placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000"
              value={orgId}
              onChange={(e) => {
                setOrgId(e.target.value);
                if (e.target.value) setSelectedDealer(null);
              }}
            />
          </div>
          <PermissionGuard permission={{ resource: 'user', action: 'edit' }}>
            <Button
              size="sm"
              disabled={assocMutation.isPending}
            >
              Save Association
            </Button>
          </PermissionGuard>
        </Form>
      </div>

      {user.status === 'inactive' && (
        <div className="border-t border-mp-border pt-6">
          <h3 className="font-semibold mb-2 text-mp-error">Release Email Address</h3>
          <p className="text-sm text-mp-muted mb-3">
            Releasing an email address dissociates it from this account, making it available for reuse by a new account. This action cannot be undone and should only be used when an email address needs to be transferred to a new user.
          </p>
          {releaseError && (
            <Alert variant="error" className="mb-3">
              <span>{releaseError}</span>
            </Alert>
          )}
          {releaseMutation.isSuccess && (
            <Alert variant="success" className="mb-3">
              <span>Email address released successfully.</span>
            </Alert>
          )}
          <div className="mb-3">
            <Label htmlFor="release-confirm">
              Type exactly: <span className="font-mono text-sm">{RELEASE_PHRASE}</span>
            </Label>
            <Input
              id="release-confirm"
              value={releaseConfirm}
              onChange={(e) => {
                setReleaseConfirm(e.target.value);
                setReleaseError(null);
              }}
              placeholder={RELEASE_PHRASE}
            />
          </div>
          <PermissionGuard permission={{ resource: 'user', action: 'release_email' }}>
            <Button
              size="sm"
              variant="danger"
              onClick={handleRelease}
              disabled={releaseMutation.isPending || releaseConfirm !== RELEASE_PHRASE}
            >
              Release Email
            </Button>
          </PermissionGuard>
        </div>
      )}
    </div>
  );
}
